/**
 * @module hooks/useGeolocationService
 * @author Charles Blais <charles.blais@canada.ca>
 *
 * Use Geolocation API for getting user coordinates from query
 * style string.
 */
import { useEffect, useState, useMemo } from 'react';
import { AxiosResponse } from 'axios';
import useAxios from 'axios-hooks';
 
type Props = {
  initialSearch?: string,
  url?: string,
}

type Coordinates = {
  latitude: number,
  longitude: number,
};
 
type GeoLocation = {
  title: string,
  coordinates: Coordinates,
  qualifier: 'INTERPOLATED_POSITION' | 'LOCATION' | 'INTERPOLATED_CENTROID'
};

type Response = {
  data: GeoLocation[],
  loading: boolean,
  error: boolean,
  response: AxiosResponse | undefined,
  setSearch: (value: string) => void,
};
 
/**
* React Hook
* ==========
*
* Query the hazard web service and receive the response in GeoJSON
* format details.
*/
const useGeolocationService = (props: Props): Response => {

  const {
    initialSearch = '',
    url = 'https://geogratis.gc.ca/services/geolocation/en/locate',
  } = props;

  // save the search in the state
  const [ search, setSearch ] = useState<string>(initialSearch);

  const [{ data, loading, response }, refetch] = useAxios({
    url,
    params: {
      q: search,
      format: 'geojson',
    }
  }, {
    manual: search.length === 0
  });

  const geolocations: GeoLocation[] = useMemo(() => {
    if (data === undefined) return [];
    return data.map((loc: any) => {
      return {
        title: loc.title,
        coordinates: {
          latitude: loc.geometry.coordinates[1],
          longitude: loc.geometry.coordinates[0],
        }
      }
    })
  }, [data]);

  const simplifiedError: boolean = useMemo(() => {
    return Boolean(response) && geolocations.length === 0
  }, [response, geolocations]);

  // if the search has changed state, refetch the new geolocation
  useEffect(() => {
    if(search.length) {
      refetch({
        params: {
          q: search,
          format: 'geojson',
        }
      })
    }
  }, [search, refetch]);
  
  return {
    data: geolocations,
    loading,
    error: simplifiedError,
    response,
    setSearch,
  }
};

export default useGeolocationService;
